import { NavLink } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard,
  Building2,
  Briefcase,
  ClipboardList,
  MessageCircle,
  FileText,
  CalendarDays,
  Users as UsersIcon,
  BarChart3,
  LogOut,
  KeyRound,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import ChangePasswordModal from "./ChangePasswordModal";

const LINKS = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, testid: "nav-agenda" },
  { to: "/empresas-condominio", label: "Empresas de Condomínio", icon: Briefcase, testid: "nav-empresas" },
  { to: "/condominios", label: "Condomínios", icon: Building2, testid: "nav-condominios" },
  { to: "/consultas", label: "Consultas", icon: MessageCircle, testid: "nav-consultas" },
  { to: "/orcamentos", label: "Orçamentos", icon: FileText, testid: "nav-orcamentos" },
  { to: "/registros", label: "Registros", icon: ClipboardList, testid: "nav-registros" },
];

const ADMIN_LINKS = [
  { to: "/relatorios", label: "Relatórios", icon: BarChart3, testid: "nav-relatorios" },
  { to: "/admin/users", label: "Utilizadores", icon: UsersIcon, testid: "nav-admin-users" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const [pwdOpen, setPwdOpen] = useState(false);

  return (
    <aside
      className="w-64 h-screen flex-shrink-0 bg-neutral-950 text-white flex flex-col border-r border-neutral-800 sticky top-0"
      data-testid="sidebar"
    >
      <div className="px-6 py-7 border-b border-neutral-800">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#0047FF] flex items-center justify-center rounded-sm">
            <Building2 className="w-4 h-4 text-white" strokeWidth={2.2} />
          </div>
          <div>
            <div className="font-chivo font-black text-white text-base leading-none tracking-tight">
              CONDO<span className="text-[#0047FF]">CRM</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 mt-1.5 font-chivo font-bold">
              Gestão · PT
            </div>
          </div>
        </div>
      </div>

      <nav className="flex-1 py-6 overflow-y-auto">
        <div className="px-6 mb-4 text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-chivo font-bold">
          Módulos
        </div>
        <ul className="space-y-0.5">
          {LINKS.map((l) => {
            const Icon = l.icon;
            return (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.end}
                  data-testid={l.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-6 py-3 text-sm font-manrope font-medium transition-colors duration-150 ${
                      isActive
                        ? "sidebar-link-active text-white"
                        : "text-neutral-400 hover:text-white hover:bg-neutral-900"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  {l.label}
                </NavLink>
              </li>
            );
          })}
        </ul>

        {user?.role === "admin" && (
          <>
            <div className="px-6 mt-8 mb-4 text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-chivo font-bold">
              Administração
            </div>
            <ul className="space-y-0.5">
              {ADMIN_LINKS.map((l) => {
                const Icon = l.icon;
                return (
                  <li key={l.to}>
                    <NavLink
                      to={l.to}
                      data-testid={l.testid}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-6 py-3 text-sm font-manrope font-medium transition-colors duration-150 ${
                          isActive
                            ? "sidebar-link-active text-white"
                            : "text-neutral-400 hover:text-white hover:bg-neutral-900"
                        }`
                      }
                    >
                      <Icon className="w-4 h-4" strokeWidth={1.5} />
                      {l.label}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </nav>

      <div className="border-t border-neutral-800 p-4">
        <div className="flex items-center gap-3 mb-3 px-2">
          <div className="w-9 h-9 rounded-sm bg-neutral-800 flex items-center justify-center text-white font-chivo font-bold text-sm">
            {(user?.name || "A").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white font-manrope font-semibold truncate">
              {user?.name || "Utilizador"}
            </div>
            <div className="text-[11px] text-neutral-500 truncate font-manrope">{user?.email}</div>
          </div>
        </div>
        <button
          data-testid="change-password-btn"
          onClick={() => setPwdOpen(true)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 mb-2 text-xs font-manrope font-semibold text-neutral-300 border border-neutral-800 rounded-sm hover:bg-neutral-900 hover:text-white transition-colors"
        >
          <KeyRound className="w-3.5 h-3.5" strokeWidth={1.8} />
          Alterar palavra-passe
        </button>
        <button
          data-testid="logout-btn"
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-manrope font-semibold text-neutral-300 border border-neutral-800 rounded-sm hover:bg-neutral-900 hover:text-white transition-colors"
        >
          <LogOut className="w-3.5 h-3.5" strokeWidth={1.8} />
          Terminar sessão
        </button>
      </div>
      <ChangePasswordModal open={pwdOpen} onClose={() => setPwdOpen(false)} />
    </aside>
  );
}
